import React from 'react';

export type PageModule = { default: React.ComponentType };

export interface LazyPage {
  Component: React.LazyExoticComponent<React.ComponentType>;
  preload: () => Promise<PageModule>;
}

export const CHUNK_RELOAD_STORAGE_KEY = 'img-cloud:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

export const isChunkLoadError = (error: unknown): boolean => (
  error instanceof Error
  && (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message))
);

const isPrerendering = (): boolean => (
  (window as typeof window & { __IMG_CLOUD_PRERENDER__?: boolean }).__IMG_CLOUD_PRERENDER__ === true
);

/**
 * A deploy replaces every hashed chunk, so an open tab may request chunks that no longer exist.
 * Reload once to pick up the new build instead of showing the error boundary.
 */
const reloadForStaleChunks = (): boolean => {
  if (isPrerendering()) {
    return false;
  }
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
    if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

/**
 * Wraps a route import so it can be rendered with React.lazy and also preloaded
 * before the first render, which keeps the prerendered HTML from flashing a fallback.
 */
export const lazyPage = (loader: () => Promise<PageModule>): LazyPage => {
  let pending: Promise<PageModule> | null = null;

  const preload = (): Promise<PageModule> => {
    if (!pending) {
      pending = loader().catch((error: unknown) => {
        pending = null;
        if (isChunkLoadError(error) && reloadForStaleChunks()) {
          // Keep Suspense on its fallback while the page reloads.
          return new Promise<PageModule>(() => undefined);
        }
        throw error;
      });
    }
    return pending;
  };

  return {
    Component: React.lazy(preload),
    preload,
  };
};
